import { process, State } from '@progress/kendo-data-query';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { GridDataStateChangeEvent, GridPagerSettings } from '@progress/kendo-react-grid';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import matchPoColumns from 'src/assets/static/invoice_scan_manager/matchPoColumns';
import Button from 'src/components/design-system/Button';
import GridWrapper from 'src/components/GridWrapper';
import Modal from 'src/components/Modal';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import PoLineItemWizard from 'src/sections/enter_receipt/enter_receipt_wizard/PoLineItemWizard';
import useGetPoMatchesApi from 'src/services/useGetPoMatchesApi';
import { IMatchingPos } from 'src/types/MatchingPos';
import MatchDocumentTableToolBar from './MatchDocumentTableToolBar';

interface MatchPoTableProps {
  isModalOpen: boolean;
  closeModal: () => void;
  poNumber: string;
}

export const initialDataState: State = {
  skip: 0,
  take: 20,
  sort: [
    {
      field: 'hasPoAccess',
      dir: 'desc',
    },
    {
      field: 'status',
      dir: 'desc',
    },
    {
      field: 'statusDate',
      dir: 'asc',
    },
  ],
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const defaultProps = {
  tableTextId: "PO's",
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  GridPagerSettings: gridPagerSettings,
} as const;

const MatchPoTable: FC<MatchPoTableProps> = ({ isModalOpen, closeModal, poNumber }) => {
  const { company: selectedCompany } = useCompany();
  const [poLineItemsData, setPoLineItemsData] = useState<IMatchingPos>({} as IMatchingPos);
  const {
    value: isLineItemModalOpen,
    setTrue: openLineItemModal,
    setFalse: closeLineItemModal,
  } = useBoolean(false);

  const [matchPoList, setMatchPotList] = useState<IMatchingPos[]>([]);
  const [dataState, setDataState] = useState(initialDataState);
  const { getPoMatches, poMatchesLoading } = useGetPoMatchesApi();

  const { data, total } = useMemo(() => process(matchPoList, dataState), [dataState, matchPoList]);

  const PoMatches = useCallback(async () => {
    if (poNumber && selectedCompany.companyId) {
      const response = await getPoMatches({
        poNumber: poNumber,
        companyId: selectedCompany.companyId,
      });
      setMatchPotList(response);
    }
  }, [getPoMatches, poNumber, selectedCompany.companyId]);

  useEffect(() => {
    if (isModalOpen) {
      setMatchPotList([]);
      setDataState(initialDataState);
      PoMatches();
    }
  }, [isModalOpen, PoMatches]);

  const onDataStateChange = useCallback(
    (e: GridDataStateChangeEvent) => {
      setDataState(e.dataState);
    },
    [setDataState]
  );

  const cellClick = useCallback(
    (data: IMatchingPos) => {
      setPoLineItemsData(data);
      openLineItemModal();
    },
    [openLineItemModal, setPoLineItemsData]
  );

  return (
    <Modal
      title={`Showing matching PO's For #${poNumber}`}
      visible={isModalOpen}
      closeModal={closeModal}
      width="80%"
    >
      <GridWrapper
        id="poDocument"
        columns={matchPoColumns}
        total={total}
        dataSource={data}
        dataState={dataState}
        onDataStateChange={onDataStateChange}
        pageable={gridPagerSettings}
        gridToolbarChildren={
          <MatchDocumentTableToolBar dataState={dataState} setDataState={setDataState} />
        }
        height="65vh"
        fieldModifiable
        cellClick={() => ({ cellClick })}
        {...defaultProps}
        loading={poMatchesLoading}
      />

      <DialogActionsBar>
        <div className="flex justify-end mt-3">
          <Button color="info" onClick={closeModal}>
            Close
          </Button>
        </div>
      </DialogActionsBar>

      <Modal width="80%" closeModal={closeLineItemModal} visible={isLineItemModalOpen} title="">
        <PoLineItemWizard
          title={`Showing items for PO # ${poLineItemsData?.poNumber || ''}`}
          poId={poLineItemsData?.poId}
          isInvoice={false}
        />
        <DialogActionsBar>
          <div className="flex justify-end gap-2">
            <Button color="info" onClick={closeLineItemModal}>
              Close
            </Button>
          </div>
        </DialogActionsBar>
      </Modal>
    </Modal>
  );
};

export default MatchPoTable;
